import copy from '../../../copy/en/common.json' with { type: 'json' };
import { Tabs as RadixTabs } from "radix-ui";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import type { LucideIcon } from "lucide-react";
import { Link, NavLink, useLocation } from "react-router";
import { cx } from "./cx.js";

export interface TabItem {
  value: string;
  label: ReactNode;
  content: ReactNode;
  disabled?: boolean;
  icon?: LucideIcon;
}

/** In-page tabs (state, not routes). */
export function Tabs({
  items,
  defaultValue,
  value,
  onValueChange,
  ariaLabel = copy.Tabs.sections,
  keepMounted = false,
  secondary = false,
  actions,
}: {
  items: TabItem[];
  defaultValue?: string;
  value?: string;
  onValueChange?: (v: string) => void;
  ariaLabel?: string;
  /** Preserve inactive panel state and pending drafts in the DOM. */
  keepMounted?: boolean;
  secondary?: boolean;
  actions?: ReactNode;
}) {
  const [internalValue, setInternalValue] = useState(defaultValue ?? items[0]?.value);
  const selected = value ?? internalValue;
  const listRef = useActiveTabIndicator(selected, '[data-state="active"]');
  const previousSelected = useRef(selected);
  const [changed, setChanged] = useState(false);
  useLayoutEffect(() => {
    if (previousSelected.current !== selected) setChanged(true);
    previousSelected.current = selected;
  }, [selected]);
  const list = <RadixTabs.List ref={listRef} className={cx("tabs__list", secondary && "tabs__list--secondary")} aria-label={ariaLabel}>
        {items.map((t) => (
          <RadixTabs.Trigger key={t.value} value={t.value} className="tabs__trigger" disabled={t.disabled}>
            {t.icon ? <t.icon size={17} aria-hidden="true" className="section-tab__icon" /> : null}
            {t.label}
          </RadixTabs.Trigger>
        ))}
      </RadixTabs.List>;
  return (
    <RadixTabs.Root value={selected} data-tab-changed={changed ? "true" : undefined} onValueChange={(next) => { setInternalValue(next); onValueChange?.(next); }}>
      {actions ? <div className="tabs__header">{list}<div className="tabs__actions">{actions}</div></div> : list}
      {items.map((t) => (
        <RadixTabs.Content key={t.value} value={t.value} className="tabs__content" forceMount={keepMounted || undefined}>
          {t.content}
        </RadixTabs.Content>
      ))}
    </RadixTabs.Root>
  );
}

export interface SubNavItem {
  to: string;
  label: ReactNode;
  /** Small muted hint (e.g. "interim", "Phase 1"). */
  hint?: ReactNode;
  end?: boolean;
  icon?: LucideIcon;
  /** Resolve query-string routes explicitly instead of matching pathname alone. */
  active?: boolean;
}

/** Route-driven sub-navigation styled like tabs. */
export function SubNav({ items, ariaLabel, secondary = false, className }: { items: SubNavItem[]; ariaLabel: string; secondary?: boolean; className?: string }) {
  const { pathname, search } = useLocation();
  const selected = items.map(item => `${item.to}:${item.active ?? "auto"}`).join("|");
  const listRef = useActiveTabIndicator(`${pathname}${search}:${selected}`, '[aria-current="page"]');
  return (
    <nav ref={listRef} className={cx("subnav", secondary && "subnav--secondary", className)} aria-label={ariaLabel}>
      {items.map((it) => {
        const content = <>{it.icon ? <it.icon size={17} aria-hidden="true" className="section-tab__icon" /> : null}{it.label}{it.hint ? <span className="subnav__hint">{it.hint}</span> : null}</>;
        return it.active !== undefined ? <Link key={it.to} to={it.to} className={cx("subnav__link", it.active && "is-active")} aria-current={it.active ? "page" : undefined}>{content}</Link> : <NavLink
          key={it.to}
          to={it.to}
          end={it.end}
          className={({ isActive }) => cx("subnav__link", isActive && "is-active")}
        >
          {content}
        </NavLink>;
      })}
    </nav>
  );
}

/** Reveal only the horizontal selection; never move the page vertically. */
export function useActiveTabIndicator<T extends HTMLElement = HTMLDivElement>(key: string | undefined, selector: string) {
  const ref = useRef<T>(null);
  useLayoutEffect(() => {
    const list = ref.current;
    if (!list) return;
    let frame: number | undefined;
    const reveal = () => {
      const active = list.querySelector<HTMLElement>(selector);
      if (!active) { delete list.dataset.tabIndicator; return; }
      const bounds = list.getBoundingClientRect();
      const item = active.getBoundingClientRect();
      if (item.width > 0 && item.height > 0) {
        list.style.setProperty("--tab-x", `${item.left - bounds.left + list.scrollLeft - list.clientLeft}px`);
        list.style.setProperty("--tab-width", `${item.width}px`);
        list.style.setProperty("--tab-height", `${item.height}px`);
        list.dataset.tabIndicator = "ready";
        if (!list.dataset.tabMotion && frame === undefined) frame = requestAnimationFrame(() => { list.dataset.tabMotion = "ready"; });
      }
      if (item.left < bounds.left) list.scrollLeft += item.left - bounds.left;
      else if (item.right > bounds.right) list.scrollLeft += item.right - bounds.right;
    };
    reveal();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(reveal);
    observer?.observe(list);
    const active = list.querySelector<HTMLElement>(selector);
    if (active) observer?.observe(active);
    return () => { observer?.disconnect(); if (frame !== undefined) cancelAnimationFrame(frame); };
  }, [key, selector]);
  return ref;
}
